import type {
  AnalyticsEventQueryRequest,
  AnalyticsEventQueryResponse,
  AnalyticsTopConversionsQueryRequest,
  AnalyticsTopConversionsQueryResponse,
  AnalyticsTopEventsQueryRequest,
  AnalyticsTopEventsQueryResponse,
} from "@/api/analytics";
import type { TopConversionsResult, TopEventsResult } from "@/analytics/types";

import { getPreviewGraph } from "../graph";
import { EVENT_DEF_IDS } from "../graph/entities";
import {
  parseWireDateRange,
  runAnalyticsEventQuery,
} from "../graph/web-analytics";

export function handleAnalyticsEventQuery(
  request: AnalyticsEventQueryRequest,
): AnalyticsEventQueryResponse {
  const graph = getPreviewGraph();
  return runAnalyticsEventQuery({
    sessions: graph.sessions,
    previousSessions: graph.previousSessions,
    devices: graph.devices,
    windowEnd: graph.windowEnd,
    request,
  });
}

function countEventNamesInWindow(
  windowStart: number,
  windowEnd: number,
): Map<string, number> {
  const graph = getPreviewGraph();
  const counts = new Map<string, number>();
  for (const session of graph.sessions) {
    if (session.startedAt < windowStart || session.startedAt > windowEnd)
      continue;
    for (const evt of session.events) {
      if (evt.timestamp < windowStart || evt.timestamp > windowEnd) continue;
      counts.set(evt.eventName, (counts.get(evt.eventName) ?? 0) + 1);
    }
  }
  return counts;
}

export function handleAnalyticsTopConversions(
  request: AnalyticsTopConversionsQueryRequest,
): AnalyticsTopConversionsQueryResponse {
  const graph = getPreviewGraph();
  const { start, end } = parseWireDateRange(request.dateRange, graph.windowEnd);
  const counts = countEventNamesInWindow(start, end);

  const formSubmits = counts.get("form_submitted") ?? 0;
  const bookingCompletes = counts.get("appointment_booking_completed") ?? 0;
  const callInits = counts.get("call_initiated") ?? 0;

  const eventDefRows: TopConversionsResult[] = [
    {
      eventName: "Appointment Booking Completed",
      eventType: null,
      eventDefId: EVENT_DEF_IDS.appointment_booking_completed,
      conversions: bookingCompletes,
    },
    {
      eventName: "Form Submitted",
      eventType: null,
      eventDefId: EVENT_DEF_IDS.form_submitted,
      conversions: formSubmits,
    },
    {
      eventName: "Call Initiated",
      eventType: null,
      eventDefId: EVENT_DEF_IDS.call_initiated,
      conversions: callInits,
    },
  ];

  // Destination-tagged conversions are derived from the same booking signal
  // — fan out a fraction of bookings to each ad platform so the destination
  // breakdown looks plausible without a real delivery pipeline.
  const destRows: TopConversionsResult[] = [
    {
      eventName: "Appointment Booked",
      eventType: "server",
      eventDefId: null,
      conversions: Math.round(bookingCompletes * 0.6),
    },
    {
      eventName: "Lead Submitted",
      eventType: "server",
      eventDefId: null,
      conversions: Math.round(formSubmits * 0.45),
    },
    {
      eventName: "Form Submitted",
      eventType: "server",
      eventDefId: null,
      conversions: Math.round(formSubmits * 0.18),
    },
  ];

  const rows = [...eventDefRows, ...destRows]
    .filter((r) => (r.conversions ?? 0) > 0)
    .sort((a, b) => (b.conversions ?? 0) - (a.conversions ?? 0));

  const offset = request.offset ?? 0;
  const limit = request.limit ?? 10;
  return rows.slice(offset, offset + limit);
}

export function handleAnalyticsTopEvents(
  request: AnalyticsTopEventsQueryRequest,
): AnalyticsTopEventsQueryResponse {
  const graph = getPreviewGraph();
  const { start, end } = parseWireDateRange(request.dateRange, graph.windowEnd);
  const counts = countEventNamesInWindow(start, end);

  // Map each non-page_view event with a known event_def to a top-events row.
  const rows: TopEventsResult[] = [];
  for (const def of graph.eventDefs) {
    if (def.name === "Page View") continue;
    const internalName = Object.keys(EVENT_DEF_IDS).find(
      (k) => EVENT_DEF_IDS[k as keyof typeof EVENT_DEF_IDS] === def.eventDefId,
    );
    if (!internalName) continue;
    const count = counts.get(internalName) ?? 0;
    if (count === 0) continue;
    rows.push({
      eventDefId: def.eventDefId,
      eventName: def.name,
      eventType: "manual_tracked",
      events: count,
    });
  }

  rows.sort((a, b) => b.events - a.events);
  const offset = request.offset ?? 0;
  const limit = request.limit ?? 10;
  return rows.slice(offset, offset + limit);
}
